import { Link } from "react-router-dom";
import { BOOKS, SITE, FOOTER_CHECKSUM } from "@/data/series";
import { RedactedText } from "@/components/redaction/RedactedText";
import { ManualTransmit } from "@/components/secrets/ManualTransmit";
import { useSecrets } from "@/context/SecretsContext";
import { useStore } from "@/context/StoreContext";
import { StorePicker } from "@/components/StorePicker";

export const SiteFooter = () => {
  const { activeEggs, eggsFound } = useSecrets();
  const { code, kindleUrl, paperbackUrl, authorUrl, urlFor } = useStore();
  const bookTwo = BOOKS.find((b) => b.number === 2);
  const bookTwoUrl = bookTwo?.asin ? urlFor(bookTwo.asin) : SITE.bookTwoUrl;

  return (
    <footer
      data-testid="footer"
      className="relative z-10 border-t border-border bg-[hsl(220_18%_5%)]"
    >
      <div className="mx-auto max-w-6xl px-4 py-10 sm:px-6 sm:py-12">
        <div className="flex flex-col gap-8 md:flex-row md:items-start md:justify-between">
          <div className="max-w-sm">
            <p className="text-base font-semibold tracking-tight">
              {SITE.seriesTitle}
            </p>
            <p
              className="mt-1 font-mono text-[11px] uppercase tracking-[0.16em] text-[hsl(215_10%_58%)]"
              data-testid="footer-author-credit"
            >
              A six-book series by{" "}
              <a
                href={authorUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-author-link"
                className="text-[hsl(174_62%_64%)] transition-colors duration-150 hover:text-[hsl(174_62%_74%)]"
              >
                {SITE.author}
              </a>
            </p>
            <p className="mt-2 text-sm text-muted-foreground">
              <RedactedText text={SITE.subLogline} />
            </p>
            <p className="mt-4 font-mono text-[11px] uppercase tracking-[0.16em] text-[hsl(215_10%_48%)]">
              {SITE.domain}
            </p>
          </div>

          <div className="flex flex-col gap-2">
            <p className="eyebrow">Access</p>
            <a
              href={kindleUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-buy-link"
              className="font-mono text-[12px] text-[hsl(174_62%_64%)] transition-colors duration-150 hover:text-[hsl(174_62%_74%)]"
            >
              Book One on Kindle
            </a>
            <a
              href={paperbackUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-paperback-link"
              className="font-mono text-[12px] text-[hsl(32_92%_66%)] transition-colors duration-150 hover:text-[hsl(32_92%_76%)]"
            >
              Book One in paperback
            </a>
            <a
              href={bookTwoUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-book-two-link"
              className="font-mono text-[12px] text-[hsl(174_62%_64%)] transition-colors duration-150 hover:text-[hsl(174_62%_74%)]"
            >
              Book Two — The Weight of Blood
            </a>
            <StorePicker className="-ml-1.5 self-start" />
            <Link
              to="/read"
              className="font-mono text-[12px] text-muted-foreground transition-colors duration-150 hover:text-foreground"
            >
              Read the opening
            </Link>
            <a
              href="/#waitlist"
              className="font-mono text-[12px] text-muted-foreground transition-colors duration-150 hover:text-foreground"
            >
              Join the signal
            </a>
            <a
              href="/#books"
              className="font-mono text-[12px] text-muted-foreground transition-colors duration-150 hover:text-foreground"
            >
              Series index
            </a>
          </div>

          <div className="max-w-xs">
            <p className="eyebrow">Integrity monitor</p>
            <p className="mt-2 font-mono text-[11px] uppercase leading-relaxed tracking-[0.13em] text-[hsl(215_10%_50%)]">
              Anomalies armed this session
              <span className="ml-2 tabular-nums text-[hsl(32_80%_66%)]">
                {activeEggs.length}
              </span>
              <span className="mx-2 text-[hsl(215_8%_34%)]">/</span>
              located
              <span
                className="ml-2 tabular-nums text-[hsl(174_62%_62%)]"
                data-testid="anomalies-found-count"
              >
                {eggsFound.length}
              </span>
            </p>
            <p className="mt-2 font-mono text-[10px] uppercase leading-relaxed tracking-[0.13em] text-[hsl(215_8%_38%)]">
              The set changes every visit. Nothing here is where it was.
            </p>
            <div className="mt-4">
              <ManualTransmit />
            </div>
          </div>
        </div>

        <div className="mt-10 flex flex-col gap-3 border-t border-border pt-6 sm:flex-row sm:items-center sm:justify-between">
          <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-[hsl(215_8%_42%)]">
            Signal integrity checksum — <RedactedText text={FOOTER_CHECKSUM} />
          </p>
          <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-[hsl(215_8%_42%)]">
            © {new Date().getFullYear()} {SITE.seriesTitle}. All rights
            reserved.
          </p>
        </div>
      </div>
    </footer>
  );
};
